/**
 * AcceptOwnership — `/ownership/accept/:token`
 *
 * Recipient lands here from the transfer email. Preview displays workspace
 * details + confirms via POST. After accept, they become the workspace
 * owner and the previous owner becomes an admin.
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Crown, Loader2, AlertCircle, Check, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AcceptOwnershipPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/workspaces/ownership/accept/${token}`);
        const data = await r.json();
        if (!r.ok) { setError(data.detail || 'Invalid or expired link'); return; }
        setPreview(data);
      } catch {
        setError('Could not load transfer details');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const r = await fetch(`${API_URL}/api/workspaces/ownership/accept/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await r.json();
      if (!r.ok) { setError(data.detail || 'Failed to accept'); return; }
      setAccepted(true);
      setTimeout(() => navigate(`/dashboard`), 1500);
    } finally { setAccepting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="accept-error">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
          <h1 className="text-2xl font-bold mb-2">Can&apos;t accept this transfer</h1>
          <p className="text-sm text-zinc-600 mb-6">{error}</p>
          <Link to="/dashboard" className="text-emerald-600 hover:underline">Go to dashboard</Link>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="accept-success">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
            <Check className="w-9 h-9 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">You&apos;re the new owner!</h1>
          <p className="text-sm text-zinc-600">Redirecting to your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-zinc-50 flex items-center justify-center px-4" data-testid="accept-ownership-page">
      <div className="max-w-md w-full bg-white rounded-2xl border border-zinc-200 shadow-sm p-8">
        <div className="flex items-center justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
            <Crown className="w-7 h-7 text-amber-600" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-center mb-2">Accept workspace ownership</h1>
        <p className="text-sm text-zinc-600 mb-6">
          <strong>{preview?.from_email || 'The current owner'}</strong> has invited you to take over
          <strong> {preview?.workspace?.name || 'this workspace'}</strong>.
        </p>

        <div className="space-y-2 text-sm bg-zinc-50 rounded-lg p-4 mb-5">
          <p className="font-medium text-zinc-900">What happens when you accept:</p>
          <ul className="list-disc list-inside text-zinc-600 space-y-1 text-xs">
            <li>You become the workspace owner</li>
            <li>The previous owner becomes an admin</li>
            <li>You&apos;re responsible for the workspace subscription &amp; AI credit pool</li>
            <li>All existing members, data, and integrations stay in place</li>
          </ul>
        </div>

        <Button
          onClick={handleAccept}
          disabled={accepting}
          className="w-full bg-amber-500 hover:bg-amber-600 text-white h-11"
          data-testid="accept-ownership-confirm"
        >
          {accepting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
          Accept ownership
        </Button>
        <p className="text-center text-xs text-zinc-400 mt-3">
          Link expires {new Date(preview?.expires_at).toLocaleString()}
        </p>
      </div>
    </div>
  );
}
